import { useEffect, useState, useRef } from 'react'
import { Navigate } from 'react-router-dom'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import DashboardLayout from '@/components/layout/DashboardLayout'
import AuthGuard from '@/components/auth/AuthGuard'
import Dashboard from '@/pages/Dashboard'
import Transactions from '@/pages/Transactions'
import Settings from '@/pages/Settings'
import Login from '@/pages/Login'
import SignUp from '@/pages/SignUp'
import Accounts from '@/pages/Accounts'
import AccountDetails from '@/pages/AccountDetails'
import Budget from '@/pages/Budget'
import Analytics from '@/pages/Analytics'
import MoneyChat from '@/pages/MoneyChat'
import { Toaster as HotToaster } from 'react-hot-toast'
import { Toaster } from 'sonner'
import { ThemeProvider } from '@/components/ThemeProvider'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'
import NotificationsPage from '@/pages/Notifications'
import { useTranslation } from 'react-i18next'
import { useProfileStore } from '@/store/profileStore'
import CategoriesSettings from '@/components/settings/CategoriesSettings'
import PayeesSettings from '@/components/settings/PayeesSettings'
import TagsSettings from '@/components/settings/TagsSettings'
import RulesSettings from '@/components/settings/RulesSettings'
import RecurringSettings from '@/components/settings/RecurringSettings'
import CurrenciesSettings from '@/components/settings/CurrenciesSettings'
import LanguageSettings from '@/components/settings/LanguageSettings'
import DataSettings from '@/components/settings/DataSettings'
import DevToolsSettings from '@/components/settings/DevToolsSettings'
import NotificationSettings from '@/components/settings/NotificationSettings'
import './i18n'
import SavingGoalsPage from '@/pages/SavingGoals'
import { OnboardingWizard } from '@/components/onboarding/OnboardingWizard'
import { Landing } from '@/pages/Landing'
import { Contact } from '@/pages/Contact'
import { ErrorBoundary } from '@/components/error/ErrorBoundary'
import { ComingSoon } from '@/pages/ComingSoon'
import { supabase } from '@/lib/supabase'
import PrivacyPolicy from '@/pages/PrivacyPolicy'
import AuthCallback from '@/pages/AuthCallback'
import MobileAuthCallback from '@/pages/MobileAuthCallback'
import { useAuthStore } from '@/store/authStore'
import ForgotPassword from '@/pages/ForgotPassword'
import ResetPassword from '@/pages/ResetPassword'
import React from 'react'
import { useProfileSetup } from '@/hooks/useProfileSetup'

// Ensure the Supabase client is initialized early
console.log('Initializing Supabase client in App.tsx');

// Add auth state check on initialization
(async () => {
  try {
    console.log('Checking Supabase auth state...');
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.error('Error retrieving auth session:', error);
    } else {
      console.log('Auth session check on load:', data.session ? 'User is authenticated' : 'No active session');
    }
  } catch (err) {
    console.error('Unexpected error checking auth state:', err);
  }
})();

function LoadingScreen() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
  );
}

// Component to load profile settings after QueryClientProvider is initialized
function ProfileSettingsLoader({ userId }: { userId?: string }) {
  // We'll only attempt to load profile settings if we have a userId
  React.useEffect(() => {
    if (userId) {
      console.log('Loading profile settings for user:', userId);
      
      // Import the profile store dynamically to avoid hook errors
      // and use the non-hook store methods directly
      import('@flows.cash/core').then(core => {
        if (typeof core.useProfileStore?.getState === 'function') {
          // Call the refreshUserProfile method from the store
          core.useProfileStore.getState().refreshUserProfile(userId)
            .catch(err => console.error('Error refreshing user profile:', err));
        }
      }).catch(err => {
        console.error('Error importing core package:', err);
      });
    }
  }, [userId]);
  
  return null;
}

export default function App() {
  const { i18n } = useTranslation()
  const profileStore = useProfileStore() as { settings?: { preferences?: { language?: string } } }
  const authStore = useAuthStore()
  const { user, isLoading: authLoading } = authStore
  const [isAuthReady, setIsAuthReady] = useState(false)
  const authInitializedRef = useRef(false)
  const authListenerRef = useRef<(() => void) | undefined>(undefined);
  const redirectLoopCountRef = useRef(0);
  const lastPathRef = useRef<string | null>(null);
  const pathTimestampRef = useRef<number>(Date.now());
  
  // Add this line to ensure profiles are created for users
  useProfileSetup();
  
  console.log('App render - user:', !!user, 'authLoading:', authLoading, 'isAuthReady:', isAuthReady);

  // Detect and fix redirect loops
  useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;
    
    // Get the current path
    const currentPath = window.location.pathname;
    const currentTimestamp = Date.now();
    
    // Check if this is a potential redirect loop
    if (lastPathRef.current === currentPath) {
      // If we're seeing the same path in less than 2 seconds, it might be a loop
      if (currentTimestamp - pathTimestampRef.current < 2000) {
        redirectLoopCountRef.current += 1;
        console.warn(`Potential redirect loop detected at ${currentPath}. Count: ${redirectLoopCountRef.current}`);
        
        // If we detect 3 or more rapid redirects to the same path, it's almost certainly a loop
        if (redirectLoopCountRef.current >= 3) {
          console.error('Redirect loop confirmed! Attempting emergency recovery...');
          
          // Emergency recovery: clear all auth state and storage
          localStorage.removeItem('auth-storage');
          sessionStorage.clear();
          
          // Clear cookies that might be causing issues
          document.cookie.split(';').forEach(cookie => {
            const [name] = cookie.trim().split('=');
            if (name && (name.includes('supabase') || name.includes('auth') || name.includes('sb-'))) {
              document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            }
          });
          
          // Force reload to /auth/login with the reset flag
          window.location.href = '/auth/login?reset=true&loop_detected=true';
          return;
        }
      } else {
        // Reset the counter if it's been more than 2 seconds since we saw this path
        redirectLoopCountRef.current = 1;
      }
    } else {
      // New path, reset counter
      redirectLoopCountRef.current = 1;
    }
    
    // Update the last path and timestamp
    lastPathRef.current = currentPath;
    pathTimestampRef.current = currentTimestamp;
    
    // Cleanup function is not needed here
  }, [window.location.pathname]);

  // Sync language with database whenever the user changes
  useEffect(() => {
    // Only run this effect when we have a valid user
    if (user?.id) {
      console.log('User logged in, syncing language from database');
      // Use the syncFromDatabase method we added to i18n
      i18n.syncFromDatabase(user.id)
        .then(lang => {
          if (lang) {
            console.log(`Successfully synchronized language from database: ${lang}`);
          }
        })
        .catch(err => {
          console.error('Error synchronizing language from database:', err);
        });
    }
  }, [user?.id]);
  
  // Sync language with user settings (as a fallback)
  useEffect(() => {
    if (profileStore.settings?.preferences?.language) {
      const prefLang = profileStore.settings.preferences.language;
      console.log(`Language preference found in profile settings: ${prefLang}`);
      
      if (i18n.language !== prefLang) {
        console.log(`Changing language from ${i18n.language} to ${prefLang} based on profile settings`);
        i18n.changeLanguage(prefLang);
      }
    }
  }, [profileStore.settings?.preferences?.language]);

  // Initialize auth only once
  useEffect(() => {
    // Skip if already initialized
    if (authInitializedRef.current) {
      console.log('Auth already initialized, skipping initialization');
      return;
    }

    console.log('Initializing auth service');
    
    async function initializeAuth() {
      try {
        // Check URL for signs of a previous loop recovery
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('loop_detected') === 'true') {
          console.log('Previous loop detected and recovery attempted. Starting with clean state.');
          // Further checks to ensure we're in a clean state could be added here
        }
      
        // Wait for auth to be ready by checking the session
        const { data: sessionData } = await supabase.auth.getSession();
        console.log('Initial session check:', !!sessionData.session);
        
        // Detect potential state inconsistency
        const hasLocalStorageUser = !!localStorage.getItem('auth-storage');
        const hasSessionStorageAuth = !!sessionStorage.getItem('auth_completed') || 
                                    !!sessionStorage.getItem('auth_in_progress');
        
        // If we have session storage auth flags but no session, it could be a bad state
        if (hasSessionStorageAuth && !sessionData.session) {
          console.warn('Detected potential auth state inconsistency. Cleaning up session storage.');
          sessionStorage.removeItem('auth_completed');
          sessionStorage.removeItem('auth_in_progress');
          sessionStorage.removeItem('auth_attempt_count');
        }
        
        // If we have local storage user but no session, it's definitely a bad state
        if (hasLocalStorageUser && !sessionData.session) {
          console.warn('Detected auth state mismatch. Local storage has user but no active session.');
          // Consider resetting the local auth store here if needed
        }
        
        // If we have a session but no local storage user, update the auth store
        if (sessionData.session && !hasLocalStorageUser) {
          console.log('Session exists but no local user data found. Forcing auth store update.');
          try {
            // Manually trigger an auth store update with the session data
            const authStore = useAuthStore.getState() as any;
            if (authStore.coreStore && sessionData.session.user) {
              authStore.coreStore.setState({
                user: sessionData.session.user,
                token: sessionData.session.access_token,
                isAuthenticated: true,
                isLoading: false,
                error: null
              });
            }
          } catch (error) {
            console.error('Error updating auth store with session data:', error);
          }
        }
        
        // Set up a central auth state listener with a slight delay to ensure Supabase has time to restore session
        setTimeout(() => {
          const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
            console.log(`Auth state changed: event = ${event}, authenticated = ${!!session}`);
            
            // Always set auth as ready
            setIsAuthReady(true);
          });
          
          // Store the unsubscribe function
          authListenerRef.current = authListener.subscription.unsubscribe;
          
          // Mark as initialized
          authInitializedRef.current = true;
          
          // Set initial auth state
          setIsAuthReady(true);
          
          console.log('Auth initialization complete');
        }, 300); // Small delay to ensure Supabase has time to restore session
      } catch (error) {
        console.error('Error during auth initialization:', error);
        // Still set isAuthReady to true to avoid getting stuck in loading state
        setIsAuthReady(true);
      }
    }

    initializeAuth();
    
    // Clean up the auth listener when the component unmounts
    return () => {
      if (authListenerRef.current) {
        console.log('Cleaning up auth listener');
        authListenerRef.current();
        authListenerRef.current = undefined;
      }
    };
  }, []); // Empty dependency array to ensure this only runs once

  // Effect to update isAuthReady based on auth store state
  useEffect(() => {
    if (!authLoading) {
      console.log('Auth store loading complete, setting isAuthReady to true');
      setIsAuthReady(true);
    }
  }, [authLoading]);

  if (!isAuthReady || authLoading) {
    console.log('App not ready yet, showing loading screen');
    return <LoadingScreen /> // Show a loading screen while auth initializes
  }

  console.log('App ready, rendering router');

  // Create router using the object-based approach instead of JSX
  const router = createBrowserRouter(
    [
      // Public Routes
      {
        errorElement: <ErrorBoundary />,
        children: [
          { path: "/", element: <ComingSoon /> },
          { path: "/landing", element: <Landing /> },
          { path: "/contact", element: <Contact /> },
          { path: "/privacy-policy", element: <PrivacyPolicy /> },
          { path: "/login", element: <Navigate to="/auth/login" replace /> },
          { path: "/signup", element: <Navigate to="/auth/signup" replace /> },
          { path: "/auth/login", element: <Login /> },
          { path: "/auth/signup", element: <SignUp /> },
          { path: "/auth/forgot-password", element: <ForgotPassword /> },
          { path: "/auth/reset-password", element: <ResetPassword /> },
          { path: "/auth/callback", element: <AuthCallback /> },
          { path: "/mobile/auth/callback", element: <MobileAuthCallback /> },
          { path: "/notifications", element: <Navigate to="/app/notifications" replace /> },
          
          // Explicit redirect for root path if user is already authenticated
          { 
            path: "/", 
            element: user ? <Navigate to="/app/dashboard" replace /> : <ComingSoon />,
          },
        ]
      },
      
      // Protected Routes
      {
        path: "/app",
        element: (
          <AuthGuard>
            <>
              <OnboardingWizard />
              <DashboardLayout />
            </>
          </AuthGuard>
        ),
        errorElement: <ErrorBoundary />,
        children: [
          { index: true, element: <Navigate to="/app/dashboard" replace /> },
          { path: "dashboard", element: <Dashboard />, errorElement: <ErrorBoundary /> },
          { path: "transactions", element: <Transactions />, errorElement: <ErrorBoundary /> },
          { path: "accounts", element: <Accounts />, errorElement: <ErrorBoundary /> },
          { path: "accounts/:accountId", element: <AccountDetails />, errorElement: <ErrorBoundary /> },
          { path: "analytics", element: <Analytics />, errorElement: <ErrorBoundary /> },
          { path: "money-chat", element: <MoneyChat />, errorElement: <ErrorBoundary /> },
          { path: "budget", element: <Budget />, errorElement: <ErrorBoundary /> },
          { path: "saving-goals", element: <SavingGoalsPage />, errorElement: <ErrorBoundary /> },
          { path: "notifications", element: <NotificationsPage />, errorElement: <ErrorBoundary /> },
          { 
            path: "settings", 
            element: <Settings />, 
            errorElement: <ErrorBoundary />,
            children: [
              { index: true, element: <div /> },
              { path: "categories", element: <CategoriesSettings />, errorElement: <ErrorBoundary /> },
              { path: "payees", element: <PayeesSettings />, errorElement: <ErrorBoundary /> },
              { path: "tags", element: <TagsSettings />, errorElement: <ErrorBoundary /> },
              { path: "rules", element: <RulesSettings />, errorElement: <ErrorBoundary /> },
              { path: "recurring", element: <RecurringSettings />, errorElement: <ErrorBoundary /> },
              { path: "currency", element: <CurrenciesSettings />, errorElement: <ErrorBoundary /> },
              { path: "language", element: <LanguageSettings />, errorElement: <ErrorBoundary /> },
              { path: "notifications", element: <NotificationSettings />, errorElement: <ErrorBoundary /> },
              { path: "data", element: <DataSettings />, errorElement: <ErrorBoundary /> },
              { path: "developer-tools", element: <DevToolsSettings />, errorElement: <ErrorBoundary /> }
            ]
          }
        ]
      }
    ],
    {
      future: {
        v7_relativeSplatPath: true
      }
    }
  );

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        {/* Load profile settings after QueryClientProvider is initialized */}
        <ProfileSettingsLoader userId={user?.id} />
        
        <RouterProvider router={router} />
        <HotToaster position="top-right" />
        <Toaster position="top-right" />
      </QueryClientProvider>
    </ThemeProvider>
  )
}